import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { PageHeader, Card, Button, Input, Select, Stat } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, TrashSimple, ArrowUp, ArrowDown, X, FolderSimple, Star, ScanSmiley, ArrowsClockwise, UploadSimple, FilePdf, FileCsv, ShareNetwork } from "@phosphor-icons/react";
import { sharePerformance } from "@/lib/sharePerformance";

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const Journal = () => {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [folders, setFolders] = useState([]);
  const [filterFolder, setFilterFolder] = useState("all");
  const [open, setOpen] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (folderId = filterFolder) => {
    setLoading(true);
    try {
      const q = folderId !== "all" ? `?folder_id=${folderId}` : "";
      const [i, s, f] = await Promise.all([
        api.get(`/journal${q}`).then(r => r.data),
        api.get(`/journal/stats${q}`).then(r => r.data),
        api.get("/journal/folders").then(r => r.data),
      ]);
      setItems(i);
      setStats(s);
      setFolders(f);
    } finally {
      setLoading(false);
    }
  }, [filterFolder]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!confirm(lang === "cs" ? "Opravdu smazat?" : "Delete this trade?")) return;
    await api.delete(`/journal/${id}`);
    toast.success(lang === "cs" ? "Smazáno" : "Deleted");
    load();
  };

  const createFolder = async () => {
    const name = prompt(lang === "cs" ? "Název složky (např. Challenge 25k):" : "Folder name (e.g. Challenge 25k):");
    if (!name?.trim()) return;
    await api.post("/journal/folders", { name: name.trim() });
    toast.success(lang === "cs" ? "Složka vytvořena" : "Folder created");
    load();
  };

  const setActive = async (id) => {
    await api.patch(`/journal/folders/${id}`, { is_active: true });
    toast.success(lang === "cs" ? "Nastaveno jako aktivní složka" : "Set as active folder");
    load();
  };

  const delFolder = async (id) => {
    if (!confirm(lang === "cs" ? "Smazat složku? Záznamy zůstanou v deníku." : "Delete folder? Entries stay in the journal.")) return;
    await api.delete(`/journal/folders/${id}`);
    if (filterFolder === id) setFilterFolder("all");
    load("all");
  };

  const exportFile = async (fmt) => {
    try {
      const q = filterFolder !== "all" ? `?folder_id=${filterFolder}` : "";
      const res = await api.get(`/journal/export/${fmt}${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trademind-denik.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === "cs" ? `Export ${fmt.toUpperCase()} stažen` : `${fmt.toUpperCase()} export downloaded`);
    } catch {
      toast.error(lang === "cs" ? "Export selhal" : "Export failed");
    }
  };

  const sharePerf = async () => {
    try {
      const folderName = filterFolder !== "all" ? folders.find((f) => f.id === filterFolder)?.name : null;
      const mode = await sharePerformance(items, lang, folderName);
      if (mode === "downloaded") toast.success(lang === "cs" ? "Karta výkonu stažena — sdílej ji kdekoli!" : "Performance card downloaded — share it anywhere!");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(lang === "cs" ? "Sdílení selhalo" : "Share failed");
    }
  };

  return (
    <div>
      <PageHeader
        title={t("journal.title")}
        sub={t("journal.sub")}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" onClick={sharePerf} data-testid="journal-share-perf" className="flex items-center gap-2">
              <ShareNetwork weight="bold" size={16} /> {lang === "cs" ? "Sdílet výkon" : "Share performance"}
            </Button>
            <Button variant="ghost" onClick={() => exportFile("pdf")} data-testid="journal-export-pdf" className="flex items-center gap-2">
              <FilePdf weight="bold" size={16} /> PDF
            </Button>
            <Button variant="ghost" onClick={() => exportFile("csv")} data-testid="journal-export-csv" className="flex items-center gap-2">
              <FileCsv weight="bold" size={16} /> CSV
            </Button>
            <Button variant="ghost" onClick={() => setOcrOpen(true)} data-testid="journal-ocr-btn" className="flex items-center gap-2">
              <ScanSmiley weight="bold" size={16} /> {lang === "cs" ? "Import z obrázku" : "Import from image"}
            </Button>
            <Button onClick={() => setOpen(true)} data-testid="journal-add-btn" className="flex items-center gap-2">
              <Plus weight="bold" size={16} /> {t("journal.add")}
            </Button>
          </div>
        }
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Prop firm folders */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="journal-folders">
          <button
            onClick={() => setFilterFolder("all")}
            data-testid="folder-all"
            className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider border transition-colors ${filterFolder === "all" ? "bg-zinc-100 text-zinc-900 border-zinc-100" : "border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}
          >
            {lang === "cs" ? "Vše" : "All"}
          </button>
          {folders.map((f) => (
            <div
              key={f.id}
              data-testid={`folder-chip-${f.id}`}
              className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono border cursor-pointer transition-colors ${filterFolder === f.id ? "bg-blue-600/20 border-blue-500/50 text-blue-300" : "border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}
              onClick={() => setFilterFolder(f.id)}
            >
              <FolderSimple weight={f.is_active ? "fill" : "bold"} size={13} className={f.is_active ? "text-blue-400" : ""} />
              <span>{f.name}</span>
              {f.is_active ? (
                <Star weight="fill" size={11} className="text-amber-400" title={lang === "cs" ? "Aktivní složka" : "Active folder"} />
              ) : (
                <button onClick={(e) => { e.stopPropagation(); setActive(f.id); }} data-testid={`folder-activate-${f.id}`} title={lang === "cs" ? "Nastavit jako aktivní" : "Set active"} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-amber-400 transition-all">
                  <Star size={11} />
                </button>
              )}
              <button onClick={(e) => { e.stopPropagation(); delFolder(f.id); }} data-testid={`folder-del-${f.id}`} className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all">
                <X weight="bold" size={11} />
              </button>
            </div>
          ))}
          <button onClick={createFolder} data-testid="folder-create-btn" className="px-2.5 py-1.5 rounded-md text-xs border border-dashed border-zinc-700 text-zinc-500 hover:text-zinc-200 hover:border-zinc-500 transition-colors flex items-center gap-1">
            <Plus weight="bold" size={12} /> {lang === "cs" ? "Složka" : "Folder"}
          </button>
        </div>
        {folders.some(f => f.is_active) && (
          <p className="text-xs text-zinc-600 font-mono -mt-3">
            {lang === "cs" ? "Nové záznamy a OCR importy automaticky spadnou do aktivní složky (★)." : "New entries and OCR imports are auto-assigned to the active folder (★)."}
          </p>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label={t("journal.winrate")} value={loading ? "..." : `${stats?.win_rate || 0}%`} testid="j-winrate" />
          <Stat label={t("journal.pf")} value={loading ? "..." : (stats?.profit_factor || 0)} testid="j-pf" />
          <Stat label={t("journal.avgrr")} value={loading ? "..." : (stats?.avg_rr || 0)} testid="j-rr" />
          <Stat label={t("journal.totalpnl")} value={loading ? "..." : `$${(stats?.total_pnl || 0).toFixed(2)}`} accent={(stats?.total_pnl || 0) >= 0 ? "green" : "red"} testid="j-pnl" />
        </div>

        <Card className="overflow-hidden">
          {items.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="journal-empty">
              <p className="text-sm">{t("journal.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-900/50 border-b border-zinc-800">
                  <tr className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                    <th className="px-4 py-3 text-left">{t("journal.instrument")}</th>
                    <th className="px-4 py-3 text-left">{t("journal.direction")}</th>
                    <th className="px-4 py-3 text-right">{t("journal.entry")}</th>
                    <th className="px-4 py-3 text-right">{t("journal.exit")}</th>
                    <th className="px-4 py-3 text-right">RR</th>
                    <th className="px-4 py-3 text-right">{t("journal.pnl")}</th>
                    <th className="px-4 py-3 text-left">{t("journal.result")}</th>
                    <th className="px-4 py-3 text-left">{lang === "cs" ? "Složka" : "Folder"}</th>
                    <th className="px-4 py-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-zinc-900 hover:bg-zinc-900/30 transition-colors" data-testid={`journal-row-${it.id}`}>
                      <td className="px-4 py-3 font-mono font-medium text-zinc-100">{it.instrument}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono ${it.direction === "BUY" ? "text-green-400 bg-green-500/10 border border-green-500/30" : "text-red-400 bg-red-500/10 border border-red-500/30"}`}>
                          {it.direction === "BUY" ? <ArrowUp size={10} weight="bold" /> : <ArrowDown size={10} weight="bold" />} {it.direction}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-zinc-300 text-right">{it.entry_price}</td>
                      <td className="px-4 py-3 font-mono text-zinc-300 text-right">{it.exit_price || "—"}</td>
                      <td className="px-4 py-3 font-mono text-zinc-300 text-right">{it.rr || "—"}</td>
                      <td className={`px-4 py-3 font-mono font-medium text-right ${(it.pnl || 0) > 0 ? "text-green-400" : (it.pnl || 0) < 0 ? "text-red-400" : "text-zinc-400"}`}>
                        {it.pnl != null ? `$${it.pnl.toFixed(2)}` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <ResultBadge result={it.result} t={t} />
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-zinc-500">
                        {folders.find(f => f.id === it.folder_id)?.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => del(it.id)} data-testid={`journal-del-${it.id}`} className="text-zinc-500 hover:text-red-400 transition-colors p-1"><TrashSimple size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {open && <JournalDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} t={t} lang={lang} />}
      {ocrOpen && <OcrDialog onClose={() => setOcrOpen(false)} onImported={() => { setOcrOpen(false); load(); }} folders={folders} t={t} lang={lang} />}
    </div>
  );
};

const ResultBadge = ({ result, t }) => {
  const map = {
    win: "text-green-400 bg-green-500/10 border-green-500/30",
    loss: "text-red-400 bg-red-500/10 border-red-500/30",
    be: "text-zinc-400 bg-zinc-500/10 border-zinc-500/30",
    open: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  };
  return <span className={`inline-block text-xs font-mono px-2 py-0.5 rounded border ${map[result] || map.open}`}>{t(`journal.result.${result || "open"}`)}</span>;
};

const OcrDialog = ({ onClose, onImported, folders, t, lang }) => {
  const [preview, setPreview] = useState(null);
  const [folderId, setFolderId] = useState(folders.find(f => f.is_active)?.id || "");
  const [importing, setImporting] = useState(false);

  const onFile = async (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      toast.error(lang === "cs" ? "Pouze PNG, JPG nebo WEBP" : "PNG, JPG or WEBP only");
      return;
    }
    setPreview(await fileToBase64(f));
  };

  const submit = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const { data } = await api.post("/journal/import-ocr", { image_base64: preview, folder_id: folderId || null });
      if (data.imported === 0) {
        toast.error(data.detail || (lang === "cs" ? "Žádné obchody nerozpoznány" : "No trades detected"));
      } else {
        toast.success(lang === "cs" ? `Importováno ${data.imported} obchodů!` : `Imported ${data.imported} trades!`);
        onImported();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "cs" ? "Import selhal" : "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ocr-dialog">
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <h3 className="text-lg font-medium text-zinc-100">{lang === "cs" ? "Import obchodů z obrázku (OCR)" : "Import trades from image (OCR)"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100 transition-colors"><X weight="bold" size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-zinc-500">
            {lang === "cs"
              ? "Nahraj screenshot historie obchodů (MT4/5, XTB, TradingView, Binance...). AI rozpozná hodnoty a vyplní deník za tebe. Jeden obrázek může obsahovat více obchodů. Import se počítá jako 1 analýza z denního limitu."
              : "Upload a screenshot of your trade history (MT4/5, XTB, TradingView, Binance...). AI extracts the values and fills your journal. One image can contain multiple trades. Import counts as 1 analysis from your daily limit."}
          </p>
          {!preview ? (
            <label data-testid="ocr-dropzone" className="border-2 border-dashed border-zinc-700 bg-zinc-900/40 hover:bg-zinc-900 hover:border-zinc-500 transition-all rounded-xl p-10 text-center cursor-pointer block">
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="ocr-input" />
              <UploadSimple weight="thin" size={40} className="mx-auto text-zinc-600 mb-2" />
              <div className="text-sm text-zinc-300">{lang === "cs" ? "Klikni nebo přetáhni screenshot" : "Click or drop a screenshot"}</div>
            </label>
          ) : (
            <div className="relative" data-testid="ocr-preview">
              <img src={preview} alt="history" className="rounded-md w-full" />
              <button onClick={() => setPreview(null)} className="absolute top-2 right-2 bg-zinc-950/80 border border-zinc-700 rounded-md p-1.5 text-zinc-300 hover:text-zinc-100"><X weight="bold" size={14} /></button>
            </div>
          )}
          <Select
            data-testid="ocr-folder"
            label={lang === "cs" ? "Uložit do složky" : "Save to folder"}
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            options={[{ value: "", label: lang === "cs" ? "— bez složky —" : "— no folder —" }, ...folders.map(f => ({ value: f.id, label: f.is_active ? `★ ${f.name}` : f.name }))]}
          />
          <Button onClick={submit} disabled={!preview || importing} data-testid="ocr-submit" className="w-full flex items-center justify-center gap-2 disabled:opacity-50">
            {importing ? <><ArrowsClockwise size={16} className="animate-spin" /> {lang === "cs" ? "Rozpoznávám obchody..." : "Extracting trades..."}</> : <><ScanSmiley weight="bold" size={16} /> {lang === "cs" ? "Importovat" : "Import"}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

const JournalDialog = ({ onClose, onSaved, t, lang }) => {
  const [form, setForm] = useState({
    instrument: "EURUSD",
    direction: "BUY",
    entry_price: "",
    exit_price: "",
    stop_loss: "",
    take_profit: "",
    lot_size: 0.1,
    pnl: "",
    notes: "",
    style: "intraday",
    result: "open",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        entry_price: parseFloat(form.entry_price),
        exit_price: form.exit_price ? parseFloat(form.exit_price) : null,
        stop_loss: form.stop_loss ? parseFloat(form.stop_loss) : null,
        take_profit: form.take_profit ? parseFloat(form.take_profit) : null,
        lot_size: parseFloat(form.lot_size),
        pnl: form.pnl ? parseFloat(form.pnl) : null,
      };
      await api.post("/journal", payload);
      toast.success(lang === "cs" ? "Obchod přidán" : "Trade added");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="journal-dialog">
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <h3 className="text-lg font-medium text-zinc-100">{t("journal.add")}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100 transition-colors"><X weight="bold" size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input data-testid="j-instrument" label={t("journal.instrument")} value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value.toUpperCase() })} required />
          <Select data-testid="j-direction" label={t("journal.direction")} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} options={[{ value: "BUY", label: "BUY" }, { value: "SELL", label: "SELL" }]} />
          <Input data-testid="j-entry" type="number" step="any" label={t("journal.entry")} value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} required />
          <Input data-testid="j-exit" type="number" step="any" label={t("journal.exit")} value={form.exit_price} onChange={(e) => setForm({ ...form, exit_price: e.target.value })} />
          <Input data-testid="j-sl" type="number" step="any" label={t("journal.sl")} value={form.stop_loss} onChange={(e) => setForm({ ...form, stop_loss: e.target.value })} />
          <Input data-testid="j-tp" type="number" step="any" label={t("journal.tp")} value={form.take_profit} onChange={(e) => setForm({ ...form, take_profit: e.target.value })} />
          <Input data-testid="j-lots" type="number" step="any" label={t("journal.lots")} value={form.lot_size} onChange={(e) => setForm({ ...form, lot_size: e.target.value })} />
          <Input data-testid="j-pnl" type="number" step="any" label={`${t("journal.pnl")} ($)`} value={form.pnl} onChange={(e) => setForm({ ...form, pnl: e.target.value })} />
          <Select data-testid="j-result" label={t("journal.result")} value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} options={[
            { value: "open", label: t("journal.result.open") },
            { value: "win", label: t("journal.result.win") },
            { value: "loss", label: t("journal.result.loss") },
            { value: "be", label: t("journal.result.be") },
          ]} />
          <Select data-testid="j-style" label={t("analyze.style")} value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} options={[
            { value: "scalping", label: t("analyze.style.scalping") },
            { value: "intraday", label: t("analyze.style.intraday") },
            { value: "swing", label: t("analyze.style.swing") },
          ]} />
          <div className="col-span-2">
            <label className="text-sm font-medium text-zinc-400 mb-1.5 block">Notes</label>
            <textarea data-testid="j-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-zinc-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none text-sm placeholder-zinc-600 resize-none" />
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={saving} data-testid="j-save">{saving ? "..." : t("common.save")}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Journal;
